import { useSessionStorage } from '@vueuse/core';
import { computed, watch } from 'vue';
import { useRoute } from 'vue-router';

export const ANDROID_WEBVIEW_PARAMS_KEY = 'hgSourceParameter';
export const ANDROID_WEBVIEW_SOURCE = 'ANDROID_WEBVIEW';

export interface AndroidWebViewParams {
  source?: string;
  tenantPlusCh?: string;
}

/**
 * Composable for managing Android WebView parameters in session storage.
 *
 * Automatically persists source and tenantPlusCh query params from Android WebView
 * to session storage on initialization, and provides reactive access to determine
 * if the Tenant+ menu item should be hidden.
 */
export function useAndroidWebViewParams() {
  const route = useRoute();

  const storedParams = useSessionStorage<AndroidWebViewParams>(
    ANDROID_WEBVIEW_PARAMS_KEY,
    {},
    {
      writeDefaults: false,
    },
  );

  const sourceQueryParam = computed(() => route.query?.source as string | undefined);
  const tenantPlusChQueryParam = computed(() => route.query?.tenantPlusCh as string | undefined);

  watch(
    [sourceQueryParam, tenantPlusChQueryParam],
    ([source, tenantPlusCh]) => {
      if (source || tenantPlusCh) {
        storedParams.value = {
          ...storedParams.value,
          ...(source && { source }),
          ...(tenantPlusCh && { tenantPlusCh }),
        };
      }
    },
    { immediate: true },
  );

  const source = computed(() => sourceQueryParam.value ?? storedParams.value.source);
  const tenantPlusCh = computed(
    () => tenantPlusChQueryParam.value ?? storedParams.value.tenantPlusCh,
  );

  const shouldHideMyTenantPlusMenuItem = computed(() => {
    return source.value === ANDROID_WEBVIEW_SOURCE && tenantPlusCh.value !== 'true';
  });

  return {
    source,
    tenantPlusCh,
    shouldHideMyTenantPlusMenuItem,
  };
}
